// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasCartContext, withCart } from "@insite/client-framework/Components/CartContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import changeCustomerContext from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import placeOrder from "@insite/client-framework/Store/Pages/SavedOrderDetails/Handlers/PlaceOrder";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { CartModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { resetRetainScrollPosition } from "@mcmc/XDL/Utilities";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

interface OwnProps {
    isOpen: boolean;
    extendedStyles?: SuggestedAddressesModalStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapStateToProps = (state: McMcApplicationState) => ({
    cartId: state.pages.checkoutShipping.cartId,
    normalizedAdresses: state.xCustomCheckoutShipping.validateAddressResult?.normalizedAddresses,
    reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
    overrideAddress: state.xCustomCheckoutShipping.overrideAddressFormState?.address,
    countries: getCurrentCountries(state),
    nextCheckoutStep: state.xCustomCheckoutShipping.nextStepHash,
    location: getLocation(state),
});

const mapDispatchToProps = {
    setAddress: (address: ShipToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "CustomCheckoutShipping/SetOverrideAddress",
            address,
        } as any);
    },
    updateCart: makeHandlerChainAwaitable(updateCart),
    closeModal: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "CustomCheckoutShipping/HideValidationModal",
        } as any);
    },
};

export interface SuggestedAddressesModalStyles {
    modal?: ModalPresentationProps;
    form?: InjectableCss;
    continueButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

export const suggestedAddressesModalStyles: SuggestedAddressesModalStyles = {
    cancelButton: { variant: "secondary" },
};

const StyledForm = getStyledWrapper("form");

const SuggestedAddressesModal: React.FC<Props> = ({
    isOpen,
    extendedStyles,
    normalizedAdresses,
    history,
    updateCart,
    reviewAndSubmitPageLink,
    cartId,
    nextCheckoutStep,
    location,
    setAddress,
    overrideAddress,
    closeModal,
    countries,
}) => {
    const [styles] = React.useState(() => mergeToNew(suggestedAddressesModalStyles, extendedStyles));
    const [selectedAddressId, setSelectedAddressId] = React.useState("");
    const [selectedAddressIdError, setSelectedAddressIdError] = React.useState("");

    const continueCheckout = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (selectedAddressId) {
            const selectedAddress = normalizedAdresses?.find(address => address.id === selectedAddressId);
            const selectedCountry = countries?.find(country => country.id === overrideAddress?.country?.id);
            const parsedQuery = parseQueryString<{ express?: boolean }>(location.search);

            await setAddress({
                ...overrideAddress,
                address1: selectedAddress?.address1,
                address2: selectedAddress?.address2,
                city: selectedAddress?.city,
                postalCode: selectedAddress?.postalCode,
                state: selectedCountry?.states?.find(state => state.abbreviation === selectedAddress?.state),
            } as any);
            await updateCart({});
            let checkoutStepHash = nextCheckoutStep ? `step=${nextCheckoutStep}` : "";
            checkoutStepHash = checkoutStepHash && (cartId ? `&${checkoutStepHash}` : `?${checkoutStepHash}`);
            let continueUrl = cartId
                ? `${reviewAndSubmitPageLink!.url}?cartId=${cartId}${checkoutStepHash}`
                : `${reviewAndSubmitPageLink!.url}${checkoutStepHash}`;
            if (parsedQuery.express) {
                continueUrl += `${cartId || checkoutStepHash ? "&" : "?"}express=true`;
            }

            resetRetainScrollPosition(continueUrl);
            history.replace(continueUrl);
            closeModal();
        } else {
            setSelectedAddressIdError(translate("Select Address To Continue"));
        }
    };

    const modalCloseHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setSelectedAddressIdError("");
        closeModal();
    };

    const handleSelectAddress = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedAddressId(event.target.value);
    };

    const hiddenRadioLabel = <VisuallyHidden>{translate("Sugessted Addresses")}</VisuallyHidden>;

    return (
        <Modal
            {...styles.modal}
            headline={translate("Address Validation Results")}
            isOpen={isOpen}
            handleClose={modalCloseHandler}
        >
            <Typography>{siteMessage("McMc_SelectNormalizedAddress")}</Typography>
            <StyledForm {...styles.form} onSubmit={continueCheckout} noValidate>
                <RadioGroup
                    value={selectedAddressId}
                    label={hiddenRadioLabel}
                    onChangeHandler={event => {
                        handleSelectAddress(event);
                    }}
                    error={!selectedAddressId && selectedAddressIdError}
                >
                    {normalizedAdresses?.map(address => (
                        <Radio
                            value={address.id}
                            key={address.id}
                            data-test-selector="savedOrderDetailsAddressModal_useFromOrder"
                        >
                            <AddressInfoDisplay {...address} />
                        </Radio>
                    ))}
                </RadioGroup>
                <StyledWrapper className="modal-actions">
                    <Button
                        {...styles.continueButton}
                        type="submit"
                        data-test-selector="savedOrderDetailsAddressModal_continueButton"
                    >
                        {translate("Continue")}
                    </Button>
                    <Button {...styles.cancelButton} onClick={modalCloseHandler}>
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </StyledForm>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(SuggestedAddressesModal));
